import { useCallback, useEffect, useMemo, useState } from 'react';
import { dateValidatorByFormat, Field } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import * as yup from 'yup';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import useEmployerTerminationBanner from '@/modules/core/hooks/useEmployerTerminationBanner';
import { getDateIsoString } from '@/utils/modifiers/getDateIsoString';

import { usePlanTerminationModalStore, usePlanTerminationSuccessModalStore } from '../../store';
import { TerminationSteps } from '../../store/usePlanTerminationModal.store';
import { useGetPlanCoverageTerminationDataQuery } from '../queries/useGetPlanCoverageTerminationData.query';
import { useGetPlanTerminationPropertiesQuery } from '../queries/useGetPlanTerminationProperties.query';
import { useTerminatePlanQuery } from '../queries/useTerminatePlan.query';

import { usePlanTerminationCoverageDates } from './usePlanTerminationCoverageDates';

const NOT_APPLICABLE = 'NOT_APPLICABLE';

const dateValidator = (dateString?: string) => {
  if (!dateString) return false;
  if (!dateValidatorByFormat(dateString, DEFAULT_DATE_FORMAT)) return false;
  return true;
};

export const dateRangeValidator = (
  endDate?: string,
  startDate?: string,
  maxDate?: string,
) => {
  if (!endDate && !startDate) return false;

  if (maxDate && dayjs(endDate).isAfter(dayjs(maxDate))) return true;

  if (startDate && dayjs(endDate).isBefore(dayjs(startDate))) return true;
};

interface FormValues {
  terminationDate?: string;
  endOfCoverage?: string;
  lastDayToSubmitClaim?: string;
  hsaTransferOfAssets?: string;
}

interface Props {
  onCancel?: (value: boolean) => void;
}

export const usePlanTermination = ({ onCancel }: Props) => {
  const [isSubmit, setSubmit] = useState(false);
  const [isServerError, setIsServerError] = useState(false);
  const [formValues, setFormValues] = useState<FormValues>({});
  const [isTerminationDateError, setIsTerminationDateError] = useState(false);
  const [isTerminationAfterErTerminationError, setIsTerminationAfterErTerminationError] = useState(false);
  const [isClaimLastDateError, setIsClaimLastDateError] = useState(false);
  const [isEndOfCoverageError, setIsEndOfCoverageError] = useState(false);
  const [isLastDateToSubmitBeforeEndOfCoverageError, setIsLastDateToSubmitBeforeEndOfCoverageError] = useState(false);

  const [dateIsFilled, setDateIsFilled] = useState(false);
  const [hsaIsFilled, setHsaIsFilled] = useState(false);

  const { terminationDate: erTerminationDate } = useEmployerTerminationBanner();

  const terminationDate = usePlanTerminationModalStore((state) => state.terminationDate);
  const planId = usePlanTerminationModalStore((state) => state.planId);
  const planName = usePlanTerminationModalStore((state) => state.planName);
  const handleSetTerminationDate = usePlanTerminationModalStore(
    (state) => state.handleSetTerminationDate
  );
  const handleSetStep = usePlanTerminationModalStore((state) => state.handleSetStep);
  const step = usePlanTerminationModalStore((state) => state.step);
  const handleChangeSuccessVisibility = usePlanTerminationSuccessModalStore(
    (state) => state.handleChangeVisibility
  );
  const handleChangeVisibility = usePlanTerminationModalStore((state) => state.handleChangeVisibility);
  const editMode = usePlanTerminationModalStore((state) => state.editMode);
  const isHsaAccount = usePlanTerminationModalStore((state) => state.isHsaAccount);
  const hsaPlanStartDate = usePlanTerminationModalStore((state) => state.hsaPlanStartDate);
  const handleSetPageReloadAfterSuccess = usePlanTerminationModalStore((state) => state.handleSetAdditionalAction);

  const isThereTerminationDate = useMemo(
    () => terminationDate || dateValidatorByFormat(formValues.terminationDate, DEFAULT_DATE_FORMAT),
    [formValues.terminationDate, terminationDate]
  );

  const currentTerminationDate = useMemo(() => {
    const currentDate = terminationDate || formValues.terminationDate;
    if (dateValidatorByFormat(currentDate, DEFAULT_DATE_FORMAT)) return currentDate;
    return undefined;
  }, [formValues, terminationDate]);

  const {
    formattedData: terminationData,
    isLoading: terminationDataLoading
  } = useGetPlanTerminationPropertiesQuery(editMode ? planId || '' : undefined);

  const isTerminationDateWasUpdated = useMemo(() => {
    if (editMode && terminationData?.terminationDate !== formValues.terminationDate) {
      return true;
    }
    return false;
  }, [editMode, formValues.terminationDate, terminationData?.terminationDate]);

  const { formattedData, isLoading } = useGetPlanCoverageTerminationDataQuery(planId, currentTerminationDate);
  const {
    terminatePlan,
    updatePlan,
    isLoading: terminationLoading,
    isUpdateLoading,
  } = useTerminatePlanQuery();

  const isEndOfCoverageDisabled = useMemo(() => !formattedData?.coverageEndDate,
    [formattedData?.coverageEndDate]);

  const modalDescription = useMemo(() => {
    return `Please note that you can edit or cancel termination configurations until ${
      terminationDate || formValues.terminationDate
    }.`;
  }, [formValues.terminationDate, terminationDate]);

  const handleSetEndOfCoverage = useCallback(
    (value: string | undefined) => {
      setFormValues((prev) => ({
        ...prev,
        endOfCoverage: value,
      }));
    },
    [setFormValues]
  );

  const handleSetLastDayToSubmitClaim = useCallback(
    (value: string | undefined) => {
      setFormValues((prev) => ({
        ...prev,
        lastDayToSubmitClaim: value,
      }));
    },
    [setFormValues]
  );

  const { coverageData, planData } = usePlanTerminationCoverageDates({
    planId,
    planName,
    endOfCoverage: formValues.endOfCoverage,
    lastDayToSubmitClaim: formValues.lastDayToSubmitClaim,
    handleSetEndOfCoverage,
    handleSetLastDayToSubmitClaim,
    isEndOfCoverageDisabled,
    isSubmitted: isSubmit,
    planStartDate: formattedData?.startDate,
    planEndDate: formattedData?.endDate,
    isClaimLastDateError: isClaimLastDateError || isLastDateToSubmitBeforeEndOfCoverageError,
    isEndOfCoverageError,
});

const minDate = useMemo(() => {
  const currentStartDate = editMode ? formattedData?.startDate : hsaPlanStartDate;
  if (currentStartDate) {
    return dayjs(new Date(currentStartDate)).format(DEFAULT_DATE_FORMAT);
  }
  return undefined;
}, [editMode, formattedData?.startDate, hsaPlanStartDate]);

  const changeValues = useCallback(
    (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      values: any,
      fields: Field[],
      setFieldsAreFilled?: (val: boolean) => void
    ) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setFormValues((prev: any) => ({ ...prev, ...values }));
      try {
        const validatorMap = fields.reduce(
          (map, field) => ({
            ...map,
            [field.name]: field.validator,
          }),
          {}
        );
        const schema = yup.object().shape(validatorMap);
        schema.validateSync(values, { abortEarly: false });
        if (setFieldsAreFilled) setFieldsAreFilled(true);
      } catch {
        if (setFieldsAreFilled) setFieldsAreFilled(false);
      }
    },
    [setFormValues]
  );

  const handleConfirmFirstStep = useCallback(() => {
    handleSetTerminationDate(formValues.terminationDate);
    handleSetStep(TerminationSteps.SET_TERMINATION_PARAMS);
  }, [formValues.terminationDate, handleSetStep, handleSetTerminationDate]);

  const handleSubmit = useCallback(async () => {
    setSubmit(true);

    if (!dateIsFilled) {
      return;
    }

    const isTerminationDateOutOfRange = dateRangeValidator(
      formValues?.terminationDate,
      minDate,
      dayjs().add(6, 'month').format(DEFAULT_DATE_FORMAT)
    );
    if (isTerminationDateOutOfRange) {
      setIsTerminationDateError(isTerminationDateOutOfRange);
      return;
    }

    const isTerminationDateAfterErTermination = dayjs(formValues.terminationDate).isAfter(dayjs(erTerminationDate));

    if (erTerminationDate && isTerminationDateAfterErTermination) {
      setIsTerminationAfterErTerminationError(true);
      return;
    }

    if (step === TerminationSteps.SET_TERMINATION_DATE) {
      handleConfirmFirstStep();
      setSubmit(false);
      return;
    }

    if (isHsaAccount && !hsaIsFilled && step === TerminationSteps.SET_TERMINATION_PARAMS) {
      return;
    }
    const isClaimLastDateValid = dateValidator(formValues.lastDayToSubmitClaim);
    const isEndOfCoverageValid = dateValidator(formValues.endOfCoverage);
    if ((!isClaimLastDateValid || !isEndOfCoverageValid) && step === TerminationSteps.SET_TERMINATION_PARAMS) {
      return;
    }

    if (dayjs(formValues.endOfCoverage).isBefore(dayjs(minDate))) {
      setIsEndOfCoverageError(true);
      return;
    }

    if (dayjs(formValues.lastDayToSubmitClaim).isBefore(dayjs(minDate))) {
      setIsClaimLastDateError(true);
      return;
    }

    if (dayjs(formValues.lastDayToSubmitClaim).isBefore(dayjs(formValues.endOfCoverage))) {
      setIsLastDateToSubmitBeforeEndOfCoverageError(true);
      return;
    }

    try {
      handleSetTerminationDate(formValues.terminationDate);
      const payload = {
        plan_id: planId as string,
        termination_date: getDateIsoString(formValues.terminationDate) as string,
        hsa_transfer_of_assets: formValues.hsaTransferOfAssets && isHsaAccount
          ? formValues.hsaTransferOfAssets
          : NOT_APPLICABLE,
        coverage_end_date: getDateIsoString(formValues.endOfCoverage) as string,
        claim_last_date: getDateIsoString(formValues.lastDayToSubmitClaim) as string,
      };
      editMode ? await updatePlan(payload) : await terminatePlan(payload);
      handleChangeVisibility(false);
      handleSetPageReloadAfterSuccess(() => location.reload());
      handleChangeSuccessVisibility(true);
    } catch {
      setIsServerError(true);
    }
  }, [
    dateIsFilled,
    formValues,
    handleConfirmFirstStep,
    step,
    handleChangeSuccessVisibility,
    handleChangeVisibility,
    handleSetTerminationDate,
    isHsaAccount,
    hsaIsFilled,
    planId,
    terminatePlan,
    handleSetPageReloadAfterSuccess,
    setIsTerminationDateError,
    setIsEndOfCoverageError,
    setIsClaimLastDateError,
    minDate,
    setIsLastDateToSubmitBeforeEndOfCoverageError,
    editMode,
    updatePlan,
    erTerminationDate,
  ]);

  const hsaTransferOfAssets = useMemo(() => {
    return terminationData?.hsaTransferOfAssets ||  formValues.hsaTransferOfAssets;
  }, [formValues.hsaTransferOfAssets, terminationData?.hsaTransferOfAssets]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel(false);
    }
  }, [onCancel]);

  const currentButtonTitle = useMemo(() => {
    if (step === TerminationSteps.SET_TERMINATION_DATE) {
      return 'Next (1/2)';
    }
    return 'Confirm (2/2)';
  }, [step]);

  const currentInitialEndOfDate = useMemo(() => {
    if (isTerminationDateWasUpdated) {
      return formattedData?.coverageEndDate;
    }
    if (terminationData?.coverageEndDate) {
      return terminationData.coverageEndDate;
    }
    if (formattedData?.coverageEndDate) {
      return formattedData?.coverageEndDate;
    }
    return undefined;
  }, [terminationData?.coverageEndDate, formattedData?.coverageEndDate, isTerminationDateWasUpdated]);

  const currentInitialClaimLastDate = useMemo(() => {
    if (isTerminationDateWasUpdated) {
      return formattedData?.claimLastDate;
    }
    if (terminationData?.claimLastDate) {
      return terminationData.claimLastDate;
    }
    if (formattedData?.claimLastDate) {
      return formattedData.claimLastDate;
    }
    return undefined;
  }, [terminationData?.claimLastDate, formattedData?.claimLastDate, isTerminationDateWasUpdated]);

  useEffect(() => {
    if (formattedData) {
      setFormValues((prev) => ({
        ...prev,
        endOfCoverage: isEndOfCoverageDisabled ? currentInitialClaimLastDate : currentInitialEndOfDate,
        lastDayToSubmitClaim: currentInitialClaimLastDate,
      }));
    }
  }, [currentInitialClaimLastDate, currentInitialEndOfDate, formattedData, isEndOfCoverageDisabled]);

  useEffect(() => {
    if (formValues?.terminationDate && isSubmit) {
      const isTerminationDateOutOfRange = dateRangeValidator(
        formValues?.terminationDate,
        minDate,
        dayjs().add(6, 'month').format(DEFAULT_DATE_FORMAT)
      );
      if (isTerminationDateOutOfRange) {
        setIsTerminationDateError(isTerminationDateOutOfRange);
      } else {
        setIsTerminationDateError(false);
      }
    }
  }, [formValues?.terminationDate, isSubmit, minDate, terminationDate]);

  useEffect(() => {
    if (formValues?.endOfCoverage && isSubmit) {
      const isEndOfCoverageOutOfRange = dayjs(formValues.endOfCoverage).isBefore(dayjs(minDate));
      setIsEndOfCoverageError(isEndOfCoverageOutOfRange);
    }
  }, [formValues.endOfCoverage, isSubmit, minDate]);

  useEffect(() => {
    if (formValues?.lastDayToSubmitClaim && isSubmit) {
      const isClaimLastDateOutOfRange = dayjs(formValues.lastDayToSubmitClaim).isBefore(dayjs(minDate));
      setIsClaimLastDateError(isClaimLastDateOutOfRange);
    }
  }, [formValues.lastDayToSubmitClaim, isSubmit, minDate]);

  useEffect(() => {
    if (formValues?.lastDayToSubmitClaim && formValues?.endOfCoverage && isSubmit) {
      const isLastDateToSubmitBeforeEndOfCoverage = dayjs(formValues.lastDayToSubmitClaim).isBefore(dayjs(formValues.endOfCoverage));
      setIsLastDateToSubmitBeforeEndOfCoverageError(isLastDateToSubmitBeforeEndOfCoverage);
    }
  }, [formValues.lastDayToSubmitClaim, formValues.endOfCoverage, isSubmit]);

  useEffect(() => {
    if (formValues?.terminationDate && isSubmit && erTerminationDate) {
      const isTerminationDateAfterErTermination = dayjs(formValues.terminationDate).isAfter(dayjs(erTerminationDate));
      setIsTerminationAfterErTerminationError(isTerminationDateAfterErTermination);
    }
  }, [formValues.terminationDate, isSubmit, erTerminationDate]);

  return {
    isSubmit,
    setSubmit,
    isServerError,
    setIsServerError,
    formValues,
    setFormValues,
    currentTerminationDate,
    terminationDate,
    handleSetEndOfCoverage,
    handleSetLastDayToSubmitClaim,
    handleConfirmFirstStep,
    handleSubmit,
    handleCancel,
    changeValues,
    coverageData,
    isLoading: isLoading || terminationDataLoading,
    step,
    modalDescription,
    isHsaAccount,
    dateIsFilled,
    setDateIsFilled,
    hsaIsFilled,
    setHsaIsFilled,
    currentButtonTitle,
    isThereTerminationDate,
    terminationLoading: terminationLoading || isUpdateLoading,
    editMode,
    hsaTransferOfAssets,
    planData,
    minDate,
    isTerminationDateError,
    isClaimLastDateError,
    isEndOfCoverageError,
    isLastDateToSubmitBeforeEndOfCoverageError,
    planId,
    planName,
    handleChangeVisibility,
    isTerminationAfterErTerminationError,
  };
};
